import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { brand } from '../brand.js';

// ── Date Utilities ────────────────────────────────────────

// Month / weekday names come from Intl.DateTimeFormat for the ACTIVE
// locale (i18n audit 2026-07-14 — the old hand-written EN arrays only
// ever rendered English calendars). Cached per locale: Intl formatter
// construction is not free and CalendarGrid renders 42 cells per month.
const NAME_CACHE = new Map();
function localeNames(locale) {
  let names = NAME_CACHE.get(locale);
  if (names) return names;
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const shortFmt = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  const fullFmt  = new Intl.DateTimeFormat(locale, { month: 'long',  timeZone: 'UTC' });
  const dayFmt   = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const monthShort = [];
  const monthFull  = [];
  for (let m = 0; m < 12; m++) {
    const d = new Date(Date.UTC(2024, m, 1));
    monthShort.push(cap(shortFmt.format(d).replace('.', '')));
    monthFull.push(cap(fullFmt.format(d)));
  }
  // Sunday-first, matching the grid layout (firstDayOfMonth uses
  // getDay()). 2024-01-07 is a Sunday; two-letter abbreviations keep
  // the historical column width ("Su Mo…" / "Do Lu…").
  const dayNarrow = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(Date.UTC(2024, 0, 7 + i));
    dayNarrow.push(cap(dayFmt.format(d).replace('.', '')).slice(0, 2));
  }
  names = { monthShort, monthFull, dayNarrow };
  NAME_CACHE.set(locale, names);
  return names;
}

// Built-in UI strings for the pickers, keyed by locale. Consumers that
// live behind react-i18next (dashboard-render's DateRangePicker
// wrapper) pass their own translated `strings` prop — these built-ins
// only serve prop-less usage (package showcase, gallery demos), so the
// visible product strings stay sourced from the apps' i18n dicts.
const PICKER_STRINGS = {
  en: {
    selectDate: 'Select a date',
    selectRange: 'Select a range',
    today: 'Today',
    clear: 'Clear',
    apply: 'Apply',
    start: 'Start',
    end: 'End',
    pending: 'pending',
    hintPickStart: 'Click a day to pick the start date',
    hintPickEnd: 'Click a day to pick the end date',
    goToStartMonth: "Go to the start date's month",
    goToEndMonth: "Go to the end date's month",
    startPending: 'Start date pending',
    endPending: 'End date pending',
  },
  es: {
    selectDate: 'Selecciona una fecha',
    selectRange: 'Selecciona un rango',
    today: 'Hoy',
    clear: 'Limpiar',
    apply: 'Aplicar',
    start: 'Inicio',
    end: 'Fin',
    pending: 'pendiente',
    hintPickStart: 'Haz clic para seleccionar la fecha de inicio',
    hintPickEnd: 'Haz clic para seleccionar la fecha de fin',
    goToStartMonth: 'Ir al mes de la fecha de inicio',
    goToEndMonth: 'Ir al mes de la fecha de fin',
    startPending: 'Fecha de inicio pendiente',
    endPending: 'Fecha de fin pendiente',
  },
  pt: {
    selectDate: 'Selecione uma data',
    selectRange: 'Selecione um intervalo',
    today: 'Hoje',
    clear: 'Limpar',
    apply: 'Aplicar',
    start: 'Início',
    end: 'Fim',
    pending: 'pendente',
    hintPickStart: 'Clique em um dia para escolher a data de início',
    hintPickEnd: 'Clique em um dia para escolher a data de fim',
    goToStartMonth: 'Ir para o mês da data de início',
    goToEndMonth: 'Ir para o mês da data de fim',
    startPending: 'Data de início pendente',
    endPending: 'Data de fim pendente',
  },
};
function pickerStrings(locale, overrides) {
  const base = PICKER_STRINGS[locale] ?? PICKER_STRINGS.en;
  return overrides ? { ...base, ...overrides } : base;
}

function daysInMonth(year, month)  { return new Date(year, month + 1, 0).getDate(); }
function firstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }
function formatDate(d, monthShort)  { return d ? `${monthShort[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : ''; }
// All date pickers render dates as YYYY-MM-DD for consistency with
// the underlying SQL filter values (which also use ISO date format)
// and to remove ambiguity when ranges cross calendar-year boundaries
// (e.g. "1 May → 25 May" was unreadable when start=2025 / end=2026).
function formatShort(d) {
  if (!d) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function isSameDay(a, b) { return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

// ── Calendar Grid (shared) ───────────────────────────────
function CalendarGrid({ viewYear, viewMonth, onPrev, onNext, renderDay, dayGridClass = 'gap-0.5', locale = 'en' }) {
  const { monthFull, dayNarrow } = localeNames(locale);
  const days     = daysInMonth(viewYear, viewMonth);
  const firstDay = firstDayOfMonth(viewYear, viewMonth);
  const cells    = Array.from({ length: 42 }, (_, i) => {
    const day = i - firstDay + 1;
    return (day < 1 || day > days) ? null : new Date(viewYear, viewMonth, day);
  });
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        {onPrev ? (
          <button onClick={onPrev} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500 hover:text-neutral-800 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
        ) : <span className="w-8" />}
        <span className="text-xs font-bold text-neutral-800">{monthFull[viewMonth]} {viewYear}</span>
        {onNext ? (
          <button onClick={onNext} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500 hover:text-neutral-800 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        ) : <span className="w-8" />}
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
        {dayNarrow.map(d => <div key={d} className="text-2xs font-semibold text-neutral-500 py-1">{d}</div>)}
      </div>
      <div className={`grid grid-cols-7 ${dayGridClass}`}>
        {cells.map((d, i) => renderDay(d, i))}
      </div>
    </>
  );
}

// ── Calendar Picker (single date) ───────────────────────
// `locale` drives the Intl month/day names AND the built-in string set
// (EN/ES); `strings` overrides individual labels — i18n-bound
// consumers pass their translated values there.
export function CalendarPicker({ selected, onSelect, label, locale = 'en', strings, min, max, disabled = false, ariaLabel }) {
  const s = pickerStrings(locale, strings);
  const { monthShort } = localeNames(locale);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [viewYear,  setViewYear]  = useState(selected?.getFullYear() || today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.getMonth()    ?? today.getMonth());
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const outOfRange = d => !d || (min && d < min) || (max && d > max);
  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else { setViewMonth(m => m - 1); } };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else { setViewMonth(m => m + 1); } };

  return (
    <div ref={ref} data-calendar-picker="" className="relative" onKeyDown={e => { if (e.key === "Escape" && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); ref.current?.querySelector("button")?.focus(); } }}>
      {label && <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">{label}</p>}
      <button type="button" disabled={disabled} aria-label={ariaLabel || label || s.selectDate} aria-haspopup="dialog" aria-expanded={open} onClick={() => { if (!open && selected) { setViewYear(selected.getFullYear()); setViewMonth(selected.getMonth()); } setOpen(!open); }}
        className={`w-full flex items-center justify-between h-8 rounded-lg border bg-white px-3 text-sm transition-all hover:border-neutral-300 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary/30 ${
          open ? 'border-primary ring-2 ring-primary/20' : 'border-control text-neutral-800'
        }`}>
        <span className={selected ? 'text-neutral-800' : 'text-neutral-500'}>{selected ? formatDate(selected, monthShort) : s.selectDate}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={brand.semantic.muted} strokeWidth="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
      </button>
      {open && (
        <div data-dropdown-surface="calendar" role="dialog" aria-label={ariaLabel || label || s.selectDate} style={{left:Math.min(0, window.innerWidth - (ref.current?.getBoundingClientRect().left ?? 0) - 268), width:256, maxWidth:"calc(100vw - 24px)"}} className="absolute top-full left-0 mt-2 z-50 w-64 rounded-xl border border-neutral-200 bg-white shadow-xl p-3">
          <CalendarGrid viewYear={viewYear} viewMonth={viewMonth} onPrev={prevMonth} onNext={nextMonth} locale={locale}
            renderDay={(d, i) => (
              <button type="button" key={i} onClick={() => { if (d) { onSelect(d); setOpen(false); } }} disabled={outOfRange(d)}
                className={`h-8 w-8 rounded-lg text-xs font-medium transition-all ${
                  !d ? 'invisible' :
                  isSameDay(d, selected)  ? 'bg-primary text-white shadow-sm' :
                  isSameDay(d, today)     ? 'font-bold text-primary-ink ring-1 ring-inset ring-primary/30' :
                  outOfRange(d) ? 'text-neutral-300 cursor-not-allowed' : 'text-neutral-800 hover:bg-primary-soft hover:text-primary-ink'
                }`}>{d?.getDate()}</button>
            )} />
          <div className="mt-2 pt-2 border-t border-neutral-100 flex justify-between">
            <button type="button" disabled={outOfRange(today)} onClick={() => { onSelect(today); setOpen(false); }}
              className="text-xs font-semibold text-primary hover:underline">{s.today}</button>
            <button type="button" onClick={() => { onSelect(null); setOpen(false); }}
              className="text-xs font-medium text-neutral-600 hover:text-neutral-800 transition-colors">{s.clear}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Date Range Picker (dual-month) ────────────────────────
// Optional `presets` prop renders a quick-pick chip row at the top
// of the popover (above the dual calendar). Each preset is
// `{ id: string, label: string }`; clicking a chip fires
// `onPresetSelect(preset)` and closes the popover. The wrapper
// component decides what value to encode for the upstream filter.
//
// Optional `embedded` prop swaps the trigger button styling to a
// dark variant so the picker reads well against a colored banner
// (matches hp-portal's GenericFilterBar embedded mode). The popover
// content stays light — popovers sit above any backdrop.
//
// Optional `placeholder` overrides the default "Selecciona un rango"
// trigger label. Useful when a wrapper resolves a relative value
// (e.g. "Últimos 30 días") and wants to display that instead.
//
// Optional `compact` shrinks the trigger to match dense filter-bar
// rows (h-7 / px-2 / text-xs). Default is the roomier standalone
// look (h-8 / px-3 / text-sm).
//
// Optional `sliderSlot` injects a full-width row between the preset
// chips and the Inicio/Fin pills — the wrapper passes a dual-handle
// date slider wired to its own pending-selection state. The picker
// treats it as an opaque node (no layout assumptions beyond "a row").
//
// COLOR EXCEPTION (2026-07-08, accessibility): the DateRangePicker
// popover (preset chips, Inicio/Fin pills, calendar selection, day
// hover, Aplicar) intentionally stays on the PRIMARY purple family --
// matching the sidebar chrome -- instead of the magenta accent the rest
// of the catalog moved to in the v2 redesign. The user is colorblind
// and the purple selection reads far better in this dense popover.
// Do NOT sweep these `primary-*` classes back to `accent-*`.
//
// Optional `onApply` fires when the "Aplicar" button is clicked
// (before the popover closes) — the wrapper commits its pending
// slider selection there. Optional `onOpenChange(bool)` reports every
// popover open/close transition (trigger toggle, outside click, chip
// pick, Aplicar) so the wrapper can discard an uncommitted pending
// selection on dismissal.
//
// i18n (2026-07-14): `locale` picks the Intl month/day names AND the
// built-in EN/ES string set; `strings` overrides individual labels —
// the dashboard-render wrapper passes t()-resolved values from the
// apps' i18n dicts, so product strings stay dict-sourced.
//
// SELECTION FLOW (user spec 2026-07-14 — "picking a start must not
// clear the end"): while picking the START, an existing END is
// PRESERVED (`onSelect(newStart, end)`) and the active position moves
// to "end" so it can be adjusted; Apply confirms the full range. The
// ONLY case that clears the end is newStart > end — the range would be
// invalid, so the end empties and the footer asks for it. The
// Inicio/Fin pills double as re-pick triggers: clicking one navigates
// to that date's month AND arms that side as the next calendar click.
export function DateRangePicker({
  start, end, onSelect, label,
  presets, onPresetSelect, activePresetId,
  embedded = false, placeholder, compact = false,
  sliderSlot, onApply, onOpenChange,
  locale = 'en', strings,
  // When true the 520px calendar panel renders to document.body with
  // fixed positioning instead of an in-flow `absolute` popover — the
  // escape hatch for the filter cajón drawer (360px, overflow-y-auto),
  // where the panel would otherwise overflow/clip. Default false keeps
  // the historical banner behaviour byte-identical.
  portal = false,
}) {
  const s = pickerStrings(locale, strings);
  const today = new Date();
  const [viewYear,  setViewYear]  = useState(start?.getFullYear() || today.getFullYear());
  const [viewMonth, setViewMonth] = useState(start?.getMonth()    || today.getMonth());
  const [open,    setOpen]    = useState(false);
  const [picking, setPicking] = useState('start');
  const [hovered, setHovered] = useState(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState(null);

  // Position the portaled panel against the trigger: below when it
  // fits, flipped above otherwise; left clamped inside the viewport
  // (the panel is 520px wide and opens over the dimmed canvas). Mirrors
  // the InfoTip pattern — bi-components can't import dashboard-render's
  // computePopoverPosition (dep-graph direction), so the geometry is
  // duplicated here, minimal.
  const placePanel = useCallback(() => {
    if (!portal) return;
    const trigger = ref.current?.querySelector('button');
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const a = trigger.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const M = 8, GAP = 6;
    const below = a.bottom + GAP + p.height <= window.innerHeight - M;
    const top = below ? a.bottom + GAP : Math.max(M, a.top - GAP - p.height);
    const left = Math.min(
      Math.max(a.left, M),
      Math.max(M, window.innerWidth - M - p.width),
    );
    setPanelPos({ left: Math.round(left), top: Math.round(top) });
  }, [portal]);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      // Inside the trigger's subtree OR (when portaled) the panel that
      // now lives at document.body → not an outside click.
      const insideRoot = ref.current && ref.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideRoot && !insidePanel) {
        setOpen(false);
        if (onOpenChange) onOpenChange(false);
      }
    };
    // Escape closes ONLY this popover. Registered in the CAPTURE phase
    // so it runs before a host modal's own document-level (bubble)
    // Escape handler — without this, pressing Escape with the calendar
    // open closed the whole builder Preview modal and lost the filter
    // state. stopPropagation during capture prevents the bubble phase,
    // so the modal handler never fires.
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        setOpen(false);
        if (onOpenChange) onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onOpenChange]);

  // Measure + place the portaled panel before paint, and keep it glued
  // while the page scrolls under it. No-op unless `portal` is on.
  useLayoutEffect(() => {
    if (!open || !portal) { setPanelPos(null); return undefined; }
    placePanel();
    const onMove = () => placePanel();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, portal, placePanel]);

  // Right calendar = next month
  const view2Month = viewMonth === 11 ? 0  : viewMonth + 1;
  const view2Year  = viewMonth === 11 ? viewYear + 1 : viewYear;

  // Keep the selection visible: when `start` moves to a month OUTSIDE
  // the two displayed calendars (a slider drag or an external value
  // change), re-anchor the left calendar to it. Calendar clicks always
  // pick a visible day, so this never shifts the spread mid-pick.
  // Dep is the month VALUE (year*12 + month), NOT the Date identity —
  // hosts rebuild the Date object on every render, so an identity dep
  // re-runs the effect per render; under a slider drag (~100 input
  // events/s) that becomes an effect storm feeding the freeze loop.
  const startYm = start ? start.getFullYear() * 12 + start.getMonth() : null;
  useEffect(() => {
    if (startYm === null) return;
    const sY = Math.floor(startYm / 12);
    const sM = startYm % 12;
    const visible = (sY === viewYear && sM === viewMonth) ||
                    (sY === view2Year && sM === view2Month);
    if (!visible) { setViewYear(sY); setViewMonth(sM); }
  }, [startYm]);

  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else { setViewMonth(m => m - 1); } };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else { setViewMonth(m => m + 1); } };

  // UX r3: the Inicio/Fin pills double as NAVIGATION buttons — click
  // navigates the calendar pair to show that date's month without
  // touching the selection. Inicio lands its month on the LEFT
  // calendar; Fin lands its month on the RIGHT one (previous month on
  // the left for context).
  const goToMonth = (d, alignRight) => {
    if (!d) return;
    let y = d.getFullYear();
    let m = d.getMonth();
    if (alignRight) { m -= 1; if (m < 0) { m = 11; y -= 1; } }
    setViewYear(y);
    setViewMonth(m);
  };

  const handleDayClick = (d) => {
    if (!d) return;
    if (picking === 'start') {
      // Keep-the-end rule: a new start PRESERVES a still-valid end and
      // hands the active position to "end". Only start > end clears the
      // end (the range would be invalid — the UI then asks for a fin).
      if (end && d <= end) { onSelect(d, end); }
      else { onSelect(d, null); }
      setPicking('end');
    } else {
      if (d < start) { onSelect(d, start); } else { onSelect(start, d); }
      setPicking('start');
      setHovered(null);
      // stay open so user sees the highlighted range; "Aplicar" closes it
    }
  };

  // Compute range to highlight (including hover preview while picking end)
  const lo = picking === 'end' && start && hovered
    ? (start < hovered ? start : hovered)
    : start;
  const hi = picking === 'end' && start && hovered
    ? (start < hovered ? hovered : start)
    : end;

  const renderRangeDay = (d, i, key) => {
    if (!d) return <div key={`${key}-${i}`} className="h-8" />;
    const isStart  = lo && isSameDay(d, lo);
    const isEnd    = hi && isSameDay(d, hi);
    const inRange  = lo && hi && d > lo && d < hi;
    const isToday  = isSameDay(d, today);
    const isSingle = isStart && isEnd;
    const hasStrip = lo && hi && !isSingle;

    return (
      <div key={`${key}-${i}`}
        className="relative flex items-center justify-center h-8"
        onMouseEnter={() => picking === 'end' && setHovered(d)}
        onMouseLeave={() => picking === 'end' && setHovered(null)}>
        {/* continuous strip background */}
        {hasStrip && isStart && <div className="absolute inset-y-[5px] left-1/2 right-0 bg-primary/10" />}
        {hasStrip && isEnd   && <div className="absolute inset-y-[5px] left-0 right-1/2 bg-primary/10" />}
        {inRange             && <div className="absolute inset-y-[5px] left-0 right-0 bg-primary/10" />}
        {/* day circle */}
        <button
          onClick={() => handleDayClick(d)}
          className={`relative z-10 h-8 w-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all ${
            isStart || isEnd
              ? 'bg-primary text-white shadow-sm'
              : inRange
                ? 'text-primary-ink hover:bg-primary/20'
                : isToday
                  ? 'font-bold text-primary-ink ring-1 ring-inset ring-primary/30'
                  : 'text-neutral-800 hover:bg-primary-soft hover:text-primary-ink'
          }`}>
          {d.getDate()}
        </button>
      </div>
    );
  };

  const fallbackText = placeholder || s.selectRange;
  const displayText = start && end
    ? `${formatShort(start)}  →  ${formatShort(end)}`
    : start
      ? `${formatShort(start)}  →  ...`
      : fallbackText;

  const hasRange = start && end;
  // Compact mode shrinks the trigger to match dense filter-bar rows.
  // Default keeps the original (taller) standalone look.
  const heightCls   = compact ? 'h-7'  : 'h-8';
  const paddingCls  = compact ? 'px-2' : 'px-3';
  const textSizeCls = compact ? 'text-xs' : 'text-sm';
  const iconSize    = compact ? '12' : '14';
  const labelMb     = compact ? 'mb-0.5' : 'mb-1.5';
  const labelClass = embedded
    ? `text-2xs font-semibold uppercase tracking-wide text-white/75 ${labelMb}`
    : `text-2xs font-semibold uppercase tracking-wide text-neutral-500 ${labelMb}`;
  const triggerClass = embedded
    ? `w-full flex items-center justify-between ${heightCls} rounded-lg border bg-white/10 ${paddingCls} ${textSizeCls} transition-all hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-white/30 ${
        open ? 'border-white/40 ring-2 ring-white/20' : 'border-white/20 hover:border-white/30'
      }`
    : `w-full flex items-center justify-between ${heightCls} rounded-lg border bg-white ${paddingCls} ${textSizeCls} transition-all hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary/30 ${
        open ? 'border-primary ring-2 ring-primary/20' : 'border-neutral-200 hover:border-neutral-300'
      }`;
  const triggerTextClass = embedded
    ? (start || placeholder ? 'text-white' : 'text-white/75')
    : (start || placeholder ? 'text-neutral-800' : 'text-neutral-500');
  const triggerIconStroke = embedded ? 'rgba(255,255,255,0.6)' : brand.semantic.muted;

  return (
    <div ref={ref} className="relative">
      {label && <p className={labelClass}>{label}</p>}
      <button onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) setPicking('start');
          if (onOpenChange) onOpenChange(next);
        }}
        className={triggerClass}>
        <span className={`${triggerTextClass} truncate`}>{displayText}</span>
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={triggerIconStroke} strokeWidth="2" className="shrink-0"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
      </button>

      {open && ((panel) => (portal ? createPortal(panel, document.body) : panel))(
        <div
          ref={panelRef}
          data-dropdown-surface="calendar-range"
          className={portal
            ? 'fixed z-[1000] rounded-2xl border border-neutral-200 bg-white shadow-2xl'
            : 'absolute top-full left-0 mt-2 z-50 rounded-2xl border border-neutral-200 bg-white shadow-2xl'}
          style={portal
            ? { minWidth: 520, left: panelPos?.left ?? 0, top: panelPos?.top ?? 0, visibility: panelPos ? 'visible' : 'hidden' }
            : { minWidth: 520, left: Math.min(0, window.innerWidth - (ref.current?.getBoundingClientRect().left ?? 0) - Math.min(520,window.innerWidth-24) - 12) }}
        >

          {/* Quick-pick chips — optional row above the calendars */}
          {presets && presets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 border-b border-neutral-100 px-4 py-2.5">
              {presets.map(p => (
                <button
                  key={p.id}
                  onClick={() => {
                    onPresetSelect && onPresetSelect(p);
                    setOpen(false);
                    if (onOpenChange) onOpenChange(false);
                    setPicking('start');
                    setHovered(null);
                  }}
                  className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                    activePresetId === p.id
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-primary-soft hover:text-primary-ink'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
          )}

          {/* Slider row — optional, injected by the wrapper (a
              dual-handle date slider over the column's data bounds).
              Full popover width, between the chips and the pills. */}
          {sliderSlot && (
            <div className="border-b border-neutral-100 px-4 pt-3 pb-2">
              {sliderSlot}
            </div>
          )}

          {/* Step indicator — the pills are nav buttons (UX r3) AND
              re-pick triggers (keep-the-end, 2026-07-14): clicking one
              jumps the calendars to that date's month and arms that
              side, so the next day click replaces it (the other side
              is preserved). Never changes the selection by itself. */}
          <div className="flex items-center gap-2 border-b border-neutral-100 px-4 py-3">
            <button type="button"
              onClick={() => { goToMonth(start, false); setPicking('start'); setHovered(null); }}
              disabled={!start}
              aria-label={start ? `${s.goToStartMonth} (${formatShort(start)})` : s.startPending}
              title={start ? s.goToStartMonth : undefined}
              className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                picking === 'start' ? 'bg-primary text-white' : start ? 'bg-primary-soft text-primary-ink' : 'bg-neutral-100 text-neutral-600'
              } ${start ? 'cursor-pointer hover:ring-2 hover:ring-primary/30 hover:shadow-sm' : 'cursor-default'}`}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="4"/></svg>
              {s.start}: {start ? formatShort(start) : s.pending}
            </button>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={brand.neutral.axis} strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            <button type="button"
              onClick={() => { goToMonth(end, true); setPicking('end'); setHovered(null); }}
              disabled={!end}
              aria-label={end ? `${s.goToEndMonth} (${formatShort(end)})` : s.endPending}
              title={end ? s.goToEndMonth : undefined}
              className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                picking === 'end' ? 'bg-primary text-white' : end ? 'bg-primary-soft text-primary-ink' : 'bg-neutral-100 text-neutral-600'
              } ${end ? 'cursor-pointer hover:ring-2 hover:ring-primary/30 hover:shadow-sm' : 'cursor-default'}`}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="4"/></svg>
              {s.end}: {end ? formatShort(end) : s.pending}
            </button>
            <div className="ml-auto">
              <button onClick={() => { onSelect(null, null); setPicking('start'); setHovered(null); }}
                className="text-xs text-neutral-600 hover:text-neutral-800 transition-colors font-medium">{s.clear}</button>
            </div>
          </div>

          {/* Dual calendar */}
          <div data-calendar-months="" className="flex p-4 gap-4">
            <div className="flex-1">
              <CalendarGrid viewYear={viewYear} viewMonth={viewMonth} onPrev={prevMonth} onNext={null} locale={locale}
                dayGridClass="" renderDay={(d, i) => renderRangeDay(d, i, 'L')} />
            </div>
            <div className="w-px bg-neutral-100" />
            <div className="flex-1">
              <CalendarGrid viewYear={view2Year} viewMonth={view2Month} onPrev={null} onNext={nextMonth} locale={locale}
                dayGridClass="" renderDay={(d, i) => renderRangeDay(d, i, 'R')} />
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-100 px-4 py-3 flex items-center justify-between">
            <p className="text-xs text-neutral-600">
              {picking === 'start' ? s.hintPickStart : s.hintPickEnd}
            </p>
            {hasRange && (
              <button onClick={() => {
                  if (onApply) onApply();
                  setOpen(false);
                  if (onOpenChange) onOpenChange(false);
                }}
                className="rounded-lg bg-primary px-4 py-1.5 text-xs font-bold text-white hover:bg-primary-dark transition-colors shadow-sm">
                {s.apply}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Relative Date Picker ─────────────────────────────────
// Showcase-only widget (not consumed by the apps' filter bars) — its
// preset labels ship as EN/ES/PT built-ins selected by `locale`.
const RELATIVE_PRESETS = {
  en: [
    { key: 'today',     label: 'Today',              icon: '•'   },
    { key: 'yesterday', label: 'Yesterday',          icon: '←'   },
    { key: '7d',        label: 'Last 7 days',        icon: '7'   },
    { key: '30d',       label: 'Last 30 days',       icon: '30'  },
    { key: 'week',      label: 'Previous week',      icon: 'W'   },
    { key: 'month',     label: 'Previous month',     icon: 'M'   },
    { key: '3m',        label: 'Last 3 months',      icon: '3M'  },
    { key: '12m',       label: 'Last 12 months',     icon: '12M' },
    { key: 'ytd',       label: 'Year to date',       icon: 'YTD' },
  ],
  es: [
    { key: 'today',     label: 'Hoy',                icon: '•'   },
    { key: 'yesterday', label: 'Ayer',               icon: '←'   },
    { key: '7d',        label: 'Últimos 7 días',     icon: '7'   },
    { key: '30d',       label: 'Últimos 30 días',    icon: '30'  },
    { key: 'week',      label: 'Semana anterior',    icon: 'S'   },
    { key: 'month',     label: 'Mes anterior',       icon: 'M'   },
    { key: '3m',        label: 'Últimos 3 meses',    icon: '3M'  },
    { key: '12m',       label: 'Últimos 12 meses',   icon: '12M' },
    { key: 'ytd',       label: 'Año hasta la fecha', icon: 'YTD' },
  ],
  pt: [
    { key: 'today',     label: 'Hoje',               icon: '•'   },
    { key: 'yesterday', label: 'Ontem',              icon: '←'   },
    { key: '7d',        label: 'Últimos 7 dias',     icon: '7'   },
    { key: '30d',       label: 'Últimos 30 dias',    icon: '30'  },
    { key: 'week',      label: 'Semana anterior',    icon: 'S'   },
    { key: 'month',     label: 'Mês anterior',       icon: 'M'   },
    { key: '3m',        label: 'Últimos 3 meses',    icon: '3M'  },
    { key: '12m',       label: 'Últimos 12 meses',   icon: '12M' },
    { key: 'ytd',       label: 'Ano até a data',     icon: 'YTD' },
  ],
};
const RELATIVE_STRINGS = {
  en: { selectPeriod: 'Select a period', customRange: 'Custom range…' },
  es: { selectPeriod: 'Seleccionar período', customRange: 'Rango personalizado…' },
  pt: { selectPeriod: 'Selecionar período', customRange: 'Intervalo personalizado…' },
};

export function RelativeDatePicker({ value, onChange, label, locale = 'en' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const presets = RELATIVE_PRESETS[locale] ?? RELATIVE_PRESETS.en;
  const rs = RELATIVE_STRINGS[locale] ?? RELATIVE_STRINGS.en;
  const selectedPreset = presets.find(p => p.key === value);

  return (
    <div ref={ref} className="relative">
      {label && <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">{label}</p>}
      <button onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between h-8 rounded-lg border bg-white px-3 text-sm transition-all hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary/30 ${
          open ? 'border-primary ring-2 ring-primary/20' : 'border-control text-neutral-800 hover:border-neutral-400'
        }`}>
        <span className={selectedPreset ? 'text-neutral-800 font-medium' : 'text-neutral-500'}>{selectedPreset?.label || rs.selectPeriod}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={brand.semantic.muted} strokeWidth="2"
          className={`transition-transform ${open ? 'rotate-180' : ''}`}><path d="M6 9l6 6 6-6"/></svg>
      </button>
      {open && (
        <div data-dropdown-surface="list" className="absolute top-full left-0 mt-1 z-50 w-56 rounded-xl border border-neutral-200 bg-white shadow-xl overflow-hidden">
          {presets.map(p => (
            <button key={p.key} onClick={() => { onChange(p.key); setOpen(false); }}
              className={`flex items-center gap-3 w-full px-3 py-2 text-xs transition-colors ${
                value === p.key ? 'bg-primary-soft text-primary-ink font-semibold' : 'text-neutral-700 hover:bg-primary-soft'
              }`}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-md text-2xs font-bold shrink-0 ${
                value === p.key ? 'bg-primary text-white' : 'bg-neutral-100 text-neutral-600'
              }`}>{p.icon}</span>
              {p.label}
              {value === p.key && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="ml-auto shrink-0"><path d="M20 6L9 17l-5-5"/></svg>
              )}
            </button>
          ))}
          <div className="border-t border-neutral-100 px-3 py-2">
            <button className="text-xs font-semibold text-primary hover:underline">{rs.customRange}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Date Group Toggle ─────────────────────────────────────
// Showcase-only widget — EN/ES/PT option labels selected by `locale`.
const GROUP_OPTIONS = {
  en: [
    { key: 'day',     label: 'Day',       short: 'D' },
    { key: 'week',    label: 'Week',      short: 'W' },
    { key: 'month',   label: 'Month',     short: 'M' },
    { key: 'quarter', label: 'Quarter',   short: 'Q' },
    { key: 'year',    label: 'Year',      short: 'Y' },
  ],
  es: [
    { key: 'day',     label: 'Día',       short: 'D' },
    { key: 'week',    label: 'Semana',    short: 'S' },
    { key: 'month',   label: 'Mes',       short: 'M' },
    { key: 'quarter', label: 'Trimestre', short: 'T' },
    { key: 'year',    label: 'Año',       short: 'A' },
  ],
  pt: [
    { key: 'day',     label: 'Dia',       short: 'D' },
    { key: 'week',    label: 'Semana',    short: 'S' },
    { key: 'month',   label: 'Mês',       short: 'M' },
    { key: 'quarter', label: 'Trimestre', short: 'T' },
    { key: 'year',    label: 'Ano',       short: 'A' },
  ],
};

export function DateGroupToggle({ value, onChange, label, locale = 'en' }) {
  const options = GROUP_OPTIONS[locale] ?? GROUP_OPTIONS.en;
  return (
    <div>
      {label && <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">{label}</p>}
      <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5 gap-0.5">
        {options.map(o => (
          <button key={o.key} onClick={() => onChange(o.key)} title={o.label} aria-label={o.label}
            className={`h-7 rounded-md px-3 text-xs font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
              value === o.key
                ? 'bg-white text-accent-dark shadow-sm ring-1 ring-neutral-200'
                : 'text-neutral-600 hover:text-neutral-800 hover:bg-white/60'
            }`}>{o.short}</button>
        ))}
      </div>
    </div>
  );
}

// ── Freshness Badge ───────────────────────────────────────
export function FreshnessBadge({ lastUpdated, status = 'fresh' }) {
  const styles = {
    fresh: { bg: 'bg-success-soft', text: 'text-success-dark', border: 'border-success/30', dot: 'bg-success' },
    stale: { bg: 'bg-warning-soft', text: 'text-warning-dark', border: 'border-warning/30', dot: 'bg-warning' },
    error: { bg: 'bg-error-soft',   text: 'text-error-dark',   border: 'border-error/30',   dot: 'bg-error'   },
  }[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${styles.border} ${styles.bg} px-2.5 py-1 text-xs font-semibold ${styles.text}`}>
      {/* Look v3: a still dot; a pulse reads as an alert and the word already says the state. */}
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
      {lastUpdated}
    </span>
  );
}
